import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

import { CONFIG } from 'src/global-config';

import { refreshSession } from './refresh-session';

// ----------------------------------------------------------------------
// Auth do Portal do Contribuinte. A sessão vive em COOKIES httpOnly (portal_at /
// portal_rt) — NADA em localStorage/sessionStorage (imune a roubo por XSS). O
// backend valida CPF/CNPJ+senha e seta os cookies; o front só guarda o `user`.
//
// Usa `fetch` cru (credentials:'include') p/ login/me/logout — desacoplado dos
// interceptors de axios (que fazem refresh silencioso + redirect nos DADOS): no
// login um erro de senha precisa aparecer, não virar redirect.
// ----------------------------------------------------------------------

const API = `${CONFIG.serverUrl}/portal-api`;

export interface Impersonation {
  actor: string;
  actorName: string;
  returnUrl: string;
}

export interface PortalUser {
  usr_codigo: number;
  usr_login: string;
  tenant: string;
  // CPF/CNPJ do contribuinte (só dígitos) — chave de tudo no overlay da auditoria.
  // Substitui o antigo `fkEmissor` do Maker.
  cnpj: string;
  optanteId?: number | null;
  // Presente só em VISITA FISCAL: um fiscal navegando como este contribuinte.
  imp?: Impersonation | null;
}

interface AuthState {
  user: PortalUser | null;
  authenticated: boolean;
  loading: boolean;
  login: (login: string, senha: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

async function fetchMe(): Promise<PortalUser | null> {
  const r = await fetch(`${API}/auth/me`, { credentials: 'include' });
  if (r.ok) return ((await r.json()) as { user: PortalUser }).user;
  return null;
}

/**
 * Revoga a sessão no servidor SEM mexer no estado do cliente. `keepalive` garante
 * que o browser envie o POST mesmo com uma navegação top-level em andamento.
 *
 * Usado no "Voltar à Auditoria" (Visita Fiscal): diferente do `logout()` do
 * contexto, NÃO faz `setUser(null)` — isso re-renderizaria a tela de login do
 * portal por um instante antes do redirect (o flash que o fiscal via). Como o
 * fiscal está saindo do portal de vez, não há por que reidratar a UI deslogada.
 */
export function revokeSession(): void {
  try {
    void fetch(`${API}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* best-effort — a sessão de visita é time-boxed no backend de qualquer forma */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  // loading = validação inicial da sessão (enquanto true, o guard segura a rota).
  const [loading, setLoading] = useState(true);

  // Bootstrap: tenta /auth/me pelo cookie. Se o access expirou (401), faz UM
  // refresh e tenta de novo — assim reabrir o navegador dentro dos 7 dias do
  // refresh mantém logado sem redigitar senha.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let u = await fetchMe();
        if (!u && (await refreshSession())) u = await fetchMe();
        if (!cancelled) setUser(u);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (loginStr: string, senha: string) => {
    const r = await fetch(`${API}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login: loginStr, senha }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error((data as { error?: string })?.error || 'Não foi possível entrar.');
    setUser((data as { user: PortalUser }).user);
  }, []);

  const logout = useCallback(async () => {
    setUser(null);
    // Best-effort: revoga o refresh e limpa os cookies no backend.
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
    } catch {
      /* logout local já feito */
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, authenticated: !!user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}
